import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CloseIcon from "../../../../assets/icons/close-icon";
import AddButton, { DraftButton } from "../../../buttons/button";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import { trayBulkCreateApi } from "../../../../redux/apis/tray-api";
import { MAX_TRAY_BULK_COUNT, generatedTraysMessage, trayCountError } from "./tray-count";
import { errorMessage, failureMessage } from "./tray-list-state";

/**
 * "Generate Trays" dialog — same shell as ConfirmModal, with a count field.
 * One request creates every tray; each is named after its barcode ("Tray 101")
 * and can be renamed inline afterwards.
 */
const TrayGenerateModal = ({ isOpen, onClose, onGenerated }) => {
  const [count, setCount] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCount("");
      setError(null);
    }
  }, [isOpen]);

  const close = () => {
    if (!submitting) onClose();
  };

  const submit = (event) => {
    event.preventDefault();
    const problem = trayCountError(count);
    setError(problem);
    if (problem) return;
    setSubmitting(true);
    trayBulkCreateApi(Number(count.trim()))
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        const trays = res?.data?.data?.trays ?? [];
        successToast(generatedTraysMessage(trays));
        onGenerated(trays);
      })
      .catch((err) => errorToast(errorMessage(err, "Failed to generate trays")))
      .finally(() => setSubmitting(false));
  };

  return (
    <Transition show={Boolean(isOpen)} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={close}>
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <form
              onSubmit={submit}
              className="mx-auto w-full max-w-md my-8 p-6 text-left align-middle transition-all transform bg-white shadow-xl rounded-md"
            >
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium leading-6 text-gray-900">Generate Trays</h2>
                <button
                  type="button"
                  className="close-button bg-transparent border-none p-0"
                  onClick={close}
                >
                  <CloseIcon />
                </button>
              </div>

              <p className="mt-3 text-sm text-gray-600">
                Each tray gets the next barcode and is named after it, for example "Tray 101".
                You can rename them afterwards.
              </p>

              <label htmlFor="tray-generate-count" className="block mt-4 text-sm text-gray-700">
                Number of trays<span className="text-red-500">*</span>
              </label>
              <input
                id="tray-generate-count"
                autoFocus
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_TRAY_BULK_COUNT}
                step={1}
                value={count}
                placeholder={`1 – ${MAX_TRAY_BULK_COUNT}`}
                onChange={(event) => {
                  setCount(event.target.value);
                  setError(null);
                }}
                className="mt-1 h-[38px] w-full rounded-[6px] px-3 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] focus:outline-none focus:border-[var(--input-focus)]"
              />
              {error && <p className="mt-1 text-xs text-red-500">{error}</p>}

              <div className="flex justify-end gap-3 items-center mt-6">
                <DraftButton
                  className="text-primary border border-primary border-solid px-6 py-2 items-center rounded-md flex justify-center fw_500 text-sm"
                  onclkFn={close}
                  text="Cancel"
                  isDisabled={submitting}
                />
                <AddButton
                  type="submit"
                  className="bg-primary text-white px-6 py-2 rounded flex items-center justify-center fw_500 text-sm"
                  text="Generate"
                  prefixIcon={false}
                  loading={submitting}
                />
              </div>
            </form>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TrayGenerateModal;
